/**
 * WaiverGate — Globaler Re-Acceptance-Dialog
 *
 * Pollt /api/waiver/status fuer eingeloggte User. Wenn neue Version
 * vorliegt, erscheint ein blockierender Dialog mit dem neuen Volltext
 * + Buttons "Akzeptieren" / "Daten loeschen lassen". Akzeptiert der
 * User nicht und schliesst nichts an, sieht er den Dialog beim naechsten
 * Login wieder.
 *
 * Erstellt 2026-04-12 als Phase 9-Waiver.
 */

'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

interface WaiverStatus {
  needsReacceptance: boolean
  currentVersion: string | null
  requiredVersion: string
  warningCount: number
  graceUntil: string | null
  pendingDeletion: boolean
  waiverText: string | null
}

export function WaiverGate() {
  const { data: session, status } = useSession()
  const [waiverStatus, setWaiverStatus] = useState<WaiverStatus | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [requestingDelete, setRequestingDelete] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    if (status !== 'authenticated' || !session?.user) return
    fetch('/api/waiver/status')
      .then(r => r.ok ? r.json() : null)
      .then(setWaiverStatus)
      .catch(() => setWaiverStatus(null))
  }, [status, session])

  if (!waiverStatus?.needsReacceptance) return null
  if (waiverStatus.pendingDeletion) return null

  async function handleAccept() {
    setAccepting(true)
    try {
      const res = await fetch('/api/waiver/accept', { method: 'POST' })
      if (res.ok) {
        setWaiverStatus(s => s ? { ...s, needsReacceptance: false } : s)
      } else {
        alert('Speichern fehlgeschlagen — bitte später erneut versuchen.')
      }
    } finally {
      setAccepting(false)
    }
  }

  async function handleDeleteRequest() {
    setRequestingDelete(true)
    try {
      const res = await fetch('/api/waiver/delete-request', { method: 'POST' })
      if (res.ok) {
        setWaiverStatus(s => s ? { ...s, pendingDeletion: true, needsReacceptance: false } : s)
        alert('Dein Löschungs-Antrag wurde aufgenommen. Du erhältst eine Bestätigung per E-Mail.')
      } else {
        alert('Antrag konnte nicht gestellt werden. Bitte schreibe an info@modellpilot.eu.')
      }
    } finally {
      setRequestingDelete(false)
    }
  }

  const warning = waiverStatus.warningCount
  const grace = waiverStatus.graceUntil ? new Date(waiverStatus.graceUntil).toLocaleDateString('de-DE') : null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4 overflow-y-auto">
      <div className="max-w-2xl w-full my-8 rounded-[20px] bg-[var(--bg-card)] border border-[var(--accent)]/40 shadow-2xl">
        <div className="p-6 border-b border-[var(--border)]">
          <h2 className="text-xl font-black text-[var(--accent)] mb-2">
            Aktualisierte Nutzungsbedingungen
          </h2>
          <p className="text-sm text-[var(--muted)]">
            Wir haben unsere Nutzungsbedingungen und den Haftungsausschluss aktualisiert.
            Damit du FGL weiter nutzen kannst, musst du die neue Version bestätigen.
          </p>
          {warning > 0 && (
            <div className="mt-3 p-3 rounded-[10px] bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs">
              <strong>Erinnerung {warning} von 2.</strong> {grace && `Frist: ${grace}.`}
              {warning >= 2 && ' Nach Ablauf wird dein Konto automatisch deaktiviert.'}
            </div>
          )}
        </div>

        <div className="p-6 max-h-[400px] overflow-y-auto">
          <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-[var(--text)] font-sans">
            {waiverStatus.waiverText}
          </pre>
        </div>

        <div className="p-6 border-t border-[var(--border)] space-y-3">
          {!confirmDelete ? (
            <>
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full px-5 py-3 rounded-[14px] bg-[var(--accent)] text-[var(--accent-text)] font-black text-sm disabled:opacity-50"
              >
                {accepting ? 'Speichere...' : 'Ich akzeptiere die neuen Bedingungen'}
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="w-full px-5 py-3 rounded-[14px] bg-transparent border border-red-500/40 text-red-400 font-bold text-sm hover:bg-red-500/10"
              >
                Ich möchte stattdessen die Löschung meiner Daten beantragen
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-[var(--muted)] text-center mb-2">
                Bist du sicher? Wir starten dann das Lösch-Verfahren und melden uns innerhalb
                von 7 Tagen mit der Bestätigung.
              </p>
              <button
                onClick={handleDeleteRequest}
                disabled={requestingDelete}
                className="w-full px-5 py-3 rounded-[14px] bg-red-500/20 text-red-300 font-black text-sm border border-red-500/40 disabled:opacity-50"
              >
                {requestingDelete ? 'Antrag wird gesendet...' : 'Ja, Daten löschen lassen'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="w-full px-5 py-2 text-xs text-[var(--muted)] hover:text-[var(--text)]"
              >
                Zurück
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
